/**
 * @template T
 *
 * @typedef {
 *  T extends object
 *   ? T extends (infer U)[]
 *     ? KeysFromData<U>
 *     : Flatten<keyof T>
 *   : never } KeysFromData
 * @typedef { { [K in keyof T]: T[K] | null } } DataReturnType
 * @typedef { T extends object ? T[keyof T] : T } Flatten
 */

/**
 * Converte os valores nulos para null no objeto fornecido.
 *
 * @template D
 *
 * @param { D extends Object ? D : Array<D> } data - o objeto que será tratado
 * @param { KeysFromData<D> } excludeKeys - Chaves que serão ignoradas (Não receberá o tratamento)
 * @param { boolean } recursive - Se true, se o valor for uma array de objetos ou objetos, ele converterá
 * @return { DataReturnType<D> } o objeto com os dados tratados
 */
export const convertNullishToNull = (
  data,
  excludeKeys = [],
  recursive = true
) => {
  if (typeof data !== "object" && !Array.isArray(data))
    throw new Error("'convertNullishToNull': Data must be an object or array");

  if (!Array.isArray(excludeKeys))
    throw new Error("'convertNullishToNull': Exclude keys must be an array");

  if (typeof recursive !== "boolean")
    throw new Error("'convertNullishToNull': Recursive must be a boolean");

  // removendo duplicatas
  excludeKeys = Array.from(new Set(excludeKeys));

  if (Array.isArray(data)) {
    return data.map((item) => convertNullishToNull(item, excludeKeys));
  }

  return Object.keys(data).reduce((obj, key) => {
    if (excludeKeys.includes(key)) return obj;

    const value = data[key];

    if (recursive && Array.isArray(value)) {
      obj[key] = value.map((item) => convertNullishToNull(item, excludeKeys));
      return obj;
    }

    // NULL é considerado um objeto
    if (recursive && value !== null && typeof value === "object") {
      obj[key] = convertNullishToNull(value, excludeKeys);
      return obj;
    }

    if (value == "0" || Number(value)) {
      obj[key] = Number(value);
    } else {
      obj[key] = value || null;
    }

    return obj;
  }, {});
};

/**
 * @template T
 * @template S
 *
 * @param { T extends object ? T : never } target - Objeto que será transformado
 * @param { S extends object ? S : never } source - Objeto que transformará
 * @param { boolean } targetOverride - Se true, os valores no target serão substituídos
 *     pelo valor do Source se a chave coincidir. Caso contrário, os valores serão mantidos.
 * @param { boolean } convertNullish - Se true, os valores nulos serão convertidos para null.
 * @param { Array<string> } convertNullishIgnores - Se `convertNullish` for definido, chaves que serão ignoradas.
 * @param { boolean } convertRecursive - Se `convertNullish` for definido, essa opção será atribuído à ele.
 *
 * Função de converter nulos: {@link convertNullishToNull}
 *
 * @returns { { ...T, ...S } }
 */
export const mergeObjects = (
  target,
  source,
  targetOverride = false,
  convertNullish = false,
  convertNullishIgnores = [],
  convertRecursive = true
) => {
  if (typeof target !== "object" || typeof source !== "object")
    throw new Error("'mergeObjects': Target or Source must be an object");

  if (typeof targetOverride !== "boolean")
    throw new Error("'mergeObjects': targetOverride must be a boolean");

  if (typeof convertNullish !== "boolean")
    throw new Error("'mergeObjects': convertNullish must be a boolean");

  if (typeof convertRecursive !== "boolean")
    throw new Error("'mergeObjects': convertRecursive must be a boolean");

  if (!Array.isArray(convertNullishIgnores))
    throw new Error("'mergeObjects': convertNullishIgnores must be an array");

  if (targetOverride) {
    const newTarget = Object.assign(target, source);

    return convertNullish
      ? convertNullishToNull(newTarget, convertNullishIgnores, convertRecursive)
      : newTarget;
  }

  const result = Object.keys(source).reduce((obj, key) => {
    obj[key] = target[key] || source[key];

    return obj;
  }, target);

  return convertNullish
    ? convertNullishToNull(result, convertNullishIgnores, convertRecursive)
    : result;
};

export function camelCaseToSnakeCase(str) {
  return str.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);
}

export function convertObjectKeysToSnakeCase(obj) {
  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeCaseKey = camelCaseToSnakeCase(key);
      newObj[snakeCaseKey] = obj[key];
    }
  }
  return newObj;
}

export function convertToNumber(item) {
  if (item !== "" || item !== null || item !== undefined) {
    return parseInt(item);
  }

  return item;
}

export function convertToBoolean(item) {
  if (item === "true" || item === "false") {
    return item === "true";
  }

  return item;
}

export function convertToString(item) {
  if (item !== null && item !== "null" && item !== undefined) {
    return item;
  }

  return "";
}

export function setformatInBRCurrency(value) {
  const number = parseFloat(value);

  if (isNaN(number)) {
    return "R$ 0,00";
  }

  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function formatBRCurrencyToNumber(value) {
  if (typeof value === "string") {
    return value.replace("R$", "").replace(/\./g, "").replace(",", ".").trim();
  }

  return value;
}

export function ensureDecimalPlaces(value) {
  if (typeof value === "string" && !value.includes(".")) {
    return `${value}.00`;
  }
  return value;
}
